import React, { useEffect, useRef, useState } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { Block, Download, NotificationsActive, PictureAsPdf } from '@mui/icons-material';
import TableComponent from 'Component/Table';
import useApiCaller from 'hooks/apiCaller';
import FormModal from 'Component/FormModal';
import DownloadDeclarationReportForm from 'Component/MultiStepTrainingForms/TrainingForm/DownloadDeclarationReportForm';
import UploadDeclarationForm from 'Component/MultiStepTrainingForms/TrainingForm/UploadDeclarationForm';
import useCredentials from 'hooks/useCredentials';
import { toast } from 'react-toastify';

const CURRENT_YEAR = new Date().getFullYear();

const ViewDeclaration = () => {
  const [allCourses, setAllCourses] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [actionFilter, setActionFilter] = useState('all');
  const [sendingReminderKeys, setSendingReminderKeys] = useState({});

  // Bulk reminder state
  const [bulkReminderOpen, setBulkReminderOpen] = useState(false);
  const [bulkReminderDeclarationId, setBulkReminderDeclarationId] = useState('');
  const [bulkReminderStartDate, setBulkReminderStartDate] = useState('');
  const [bulkReminderEndDate, setBulkReminderEndDate] = useState('');
  const [bulkReminderMessage, setBulkReminderMessage] = useState('');
  const [isSendingBulk, setIsSendingBulk] = useState(false);

  // Individual reminder dialog state
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderRow, setReminderRow] = useState(null);
  const [reminderStartDate, setReminderStartDate] = useState('');
  const [reminderEndDate, setReminderEndDate] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // PDF download state
  const [pdfDownloadOpen, setPdfDownloadOpen] = useState(false);
  const [pdfDeclarationId, setPdfDeclarationId] = useState('');
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const apiCaller = useApiCaller();
  const apiCallerRef = useRef(apiCaller);
  const { userData: user } = useCredentials();
  apiCallerRef.current = apiCaller;

  const getReminderKey = (row) => `${row.empId}-${row.declarationId}`;

  // ── Individual reminder ──────────────────────────────────────────────────────
  const handleSendEmail = (row) => {
    setReminderRow(row);
    setReminderStartDate('');
    setReminderEndDate('');
    setReminderMessage('');
    setReminderDialogOpen(true);
  };

  const handleReminderConfirm = async () => {
    if (!reminderEndDate) {
      toast.error('Please select an end date');
      return;
    }
    const reminderKey = getReminderKey(reminderRow);
    setIsSendingReminder(true);
    try {
      setSendingReminderKeys((prev) => ({ ...prev, [reminderKey]: true }));
      const response = await apiCallerRef.current.post('/super-user/send-declaration-reminder', {
        empId: reminderRow.empId,
        declarationId: reminderRow.declarationId,
        startDate: reminderStartDate || null,
        endDate: reminderEndDate,
        message: reminderMessage || '',
      });
      const newCount = response?.data?.data?.result?.reminderCount;
      if (newCount !== undefined) {
        setAllCourses((prev) =>
          prev.map((r) =>
            r.empId === reminderRow.empId && r.declarationId === reminderRow.declarationId
              ? { ...r, reminderCount: newCount }
              : r
          )
        );
      }
      toast.success(response?.data?.message || 'Reminder email sent successfully');
      setReminderDialogOpen(false);
    } catch (error) {
      const serverMessage = error?.response?.data?.message || '';
      const isNoEmail =
        serverMessage.toLowerCase().includes('no email') ||
        serverMessage.toLowerCase().includes('email not found');
      toast.error(
        isNoEmail
          ? `Cannot send reminder: no email address found for employee ${reminderRow?.empId}`
          : serverMessage || 'Failed to send reminder email'
      );
    } finally {
      setIsSendingReminder(false);
      setSendingReminderKeys((prev) => ({ ...prev, [reminderKey]: false }));
    }
  };

  // ── Bulk reminder ────────────────────────────────────────────────────────────
  const uniqueDeclarations = Array.from(
    new Map(allCourses.map((r) => [r.declarationId, r.declarationName])).entries()
  ).map(([id, name]) => ({ id, name }));

  const handleBulkReminderSend = async () => {
    if (!bulkReminderEndDate) {
      toast.error('Please select an end date');
      return;
    }
    setIsSendingBulk(true);
    try {
      const payload = {
        ...(bulkReminderDeclarationId
          ? { declarationId: bulkReminderDeclarationId }
          : { year: selectedYear !== 'all' ? selectedYear : undefined }),
        startDate: bulkReminderStartDate || null,
        endDate: bulkReminderEndDate,
        message: bulkReminderMessage || '',
      };

      const response = await apiCallerRef.current.post(
        '/super-user/send-bulk-declaration-reminders',
        payload
      );
      toast.success(response?.data?.message || 'Bulk reminders sent successfully');
      setBulkReminderOpen(false);
      setBulkReminderDeclarationId('');
      setBulkReminderStartDate('');
      setBulkReminderEndDate('');
      setBulkReminderMessage('');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to send bulk reminders');
    } finally {
      setIsSendingBulk(false);
    }
  };

  // ── Reject declaration ───────────────────────────────────────────────────────
  const openRejectDialog = (row) => {
    setRejectRow(row);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectRow) return;
    setIsRejecting(true);
    try {
      await apiCallerRef.current.post('/super-user/reject-declaration', {
        empId: rejectRow.empId,
        declarationId: rejectRow.declarationId,
        reason: rejectReason,
      });
      toast.success('Declaration rejected and employee notified to resubmit');
      // Update local state: mark as pending (actionRequired: Yes) again
      setAllCourses((prev) =>
        prev.map((r) =>
          r.empId === rejectRow.empId && r.declarationId === rejectRow.declarationId
            ? {
                ...r,
                actionRequired: 'Yes',
                submissionStatus: 'rejected',
                rejectionReason: rejectReason,
                uploadedDocsignedURL: null,
                submittedAt: null,
              }
            : r
        )
      );
      setRejectDialogOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reject declaration');
    } finally {
      setIsRejecting(false);
    }
  };

  // ── Bulk PDF / ZIP download ──────────────────────────────────────────────────
  const handleBulkPdfDownload = async () => {
    if (!pdfDeclarationId) {
      toast.error('Please select a declaration first');
      return;
    }
    setIsDownloadingPdf(true);
    try {
      const selectedDecl = uniqueDeclarations.find((d) => d.id === pdfDeclarationId);
      const response = await apiCallerRef.current.post(
        '/super-user/download-declarations-pdf',
        { declarationId: pdfDeclarationId },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/zip' })
      );
      const link = document.createElement('a');
      link.href = url;
      const safeName = (selectedDecl?.name || 'Declarations').replace(/\s+/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `${safeName}_${dateStr}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started successfully');
      setPdfDownloadOpen(false);
      setPdfDeclarationId('');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to download declarations');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleDownloadFile = async (row) => {
    if (!row?.uploadedDocsignedURL) {
      alert('File not available');
      return;
    }
    try {
      const response = await apiCallerRef.current.post(
        '/super-user/download-declaration-file',
        { empId: row.empId, declarationId: row.declarationId },
        { responseType: 'blob' }
      );
      const contentDisposition = response.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `declaration_${row.empId}.pdf`;
      const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(row.uploadedDocsignedURL, '_blank');
    }
  };

  // ── Table columns ────────────────────────────────────────────────────────────
  const columns = [
    { label: 'EmpID', field: 'empId' },
    { label: 'Declaration Name', field: 'declarationName' },
    {
      label: 'Action Required',
      field: 'actionRequired',
      render: (row) => {
        const value = String(row.actionRequired ?? '');
        if (!value) return '-';
        const normalized =
          value.toLowerCase() === 'yes' ? 'Yes' : value.toLowerCase() === 'no' ? 'No' : value;
        const isRejected = row.submissionStatus === 'rejected';
        return (
          <Tooltip
            title={isRejected ? `Rejected: ${row.rejectionReason || 'No reason given'}` : ''}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.1,
                py: 0.35,
                borderRadius: '999px',
                backgroundColor: isRejected ? '#fee2e2' : '#f3f4f6',
                color: isRejected ? '#dc2626' : '#6b7280',
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1,
                border: `1px solid ${isRejected ? '#fca5a5' : '#e5e7eb'}`,
                userSelect: 'none',
                cursor: isRejected ? 'help' : 'default',
              }}
            >
              {isRejected ? 'Rejected' : normalized}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      label: 'Send Reminder',
      field: 'actionRequired',
      render: (row) => {
        const isYes = String(row.actionRequired).toLowerCase() === 'yes';
        const reminderKey = getReminderKey(row);
        const isSending = Boolean(sendingReminderKeys[reminderKey]);
        return isYes ? (
          <Tooltip title={isSending ? 'Sending reminder...' : 'Send reminder email'}>
            <span>
              <IconButton
                size="small"
                disabled={isSending}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendEmail(row);
                }}
                sx={{
                  width: 30,
                  height: 30,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(217, 119, 6, 0.28)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 10px rgba(180, 83, 9, 0.35)',
                  },
                  '&.Mui-disabled': { color: '#ffffff', opacity: 0.7 },
                }}
              >
                <NotificationsActive sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          '-'
        );
      },
    },
    {
      label: 'Reminders Sent',
      field: 'reminderCount',
      render: (row) => {
        const c = row.reminderCount || 0;
        return (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              height: 24,
              px: 1,
              borderRadius: '999px',
              fontSize: '0.75rem',
              fontWeight: 700,
              backgroundColor: c > 0 ? '#fef3c7' : '#f3f4f6',
              color: c > 0 ? '#b45309' : '#9ca3af',
              border: c > 0 ? '1px solid #fcd34d' : '1px solid #e5e7eb',
            }}
          >
            {c}
          </Box>
        );
      },
    },
    {
      label: 'Download Document',
      field: 'uploadedDocsignedURL',
      render: (row) =>
        row.uploadedDocsignedURL ? (
          <Tooltip title="Download document">
            <IconButton
              size="small"
              onClick={() => handleDownloadFile(row)}
              sx={{
                width: 30,
                height: 30,
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(37, 99, 235, 0.28)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 10px rgba(30, 64, 175, 0.35)',
                },
              }}
            >
              <Download sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'wrap',
              px: 1.1,
              py: 0.35,
              borderRadius: '999px',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              fontSize: '0.75rem',
              fontWeight: 600,
              lineHeight: 1,
              border: '1px solid #e5e7eb',
              userSelect: 'none',
              cursor: 'default',
            }}
          >
            Not Available
          </Box>
        ),
    },
    {
      label: 'Reject',
      field: 'reject',
      render: (row) => {
        // Only allow rejection when a valid submission exists (actionRequired = 'No')
        const hasSubmission = String(row.actionRequired).toLowerCase() === 'no';
        return hasSubmission ? (
          <Tooltip title="Reject this submission – employee will be notified to resubmit">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openRejectDialog(row);
              }}
              sx={{
                width: 30,
                height: 30,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.28)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 10px rgba(185, 28, 28, 0.35)',
                },
              }}
            >
              <Block sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          '-'
        );
      },
    },
    {
      label: 'Action',
      field: 'edit',
      render: (row) => {
        const normalizedRow = {
          ...row,
          courseId: row.courseId ?? row.declarationId,
          courseName: row.courseName ?? row.declarationName,
        };
        return (
          <FormModal
            buttonName={'Upload'}
            title={'Upload Dec Document'}
            form={<UploadDeclarationForm row={normalizedRow} user={user} />}
          />
        );
      },
    },
  ];

  // ── Data fetching ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleFetchDeclarationYears = async () => {
      try {
        const response = await apiCallerRef.current.post('/super-user/fetch-declaration-years');
        if (response) {
          const years = response?.data?.data?.result || [];
          const withCurrentYear = years.includes(CURRENT_YEAR) ? years : [CURRENT_YEAR, ...years];
          setAvailableYears(withCurrentYear);
        }
      } catch (error) {
        console.error(error);
      }
    };
    handleFetchDeclarationYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleFetchSubmitedDeclarations = async (year) => {
      try {
        const response = await apiCallerRef.current.post(
          '/super-user/fetch-all-submited-declaration',
          year && year !== 'all' ? { year } : {}
        );
        if (response) {
          setAllCourses(response?.data?.data?.result || []);
        }
      } catch (error) {
        console.error(error);
      }
    };
    handleFetchSubmitedDeclarations(selectedYear);
  }, [selectedYear]);

  const filteredCourses = (
    actionFilter === 'all'
      ? allCourses
      : allCourses.filter((row) => String(row.actionRequired).toLowerCase() === actionFilter)
  )
    .slice()
    .sort((a, b) => {
      if (!a.submittedAt && !b.submittedAt) return 0;
      if (!a.submittedAt) return 1;
      if (!b.submittedAt) return -1;
      return new Date(b.submittedAt) - new Date(a.submittedAt);
    });

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout>
      {/* Header bar */}
      <Box
        sx={{
          mb: 2,
          background: '#ffffff',
          borderRadius: '8px',
          p: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box>
          <Box sx={{ fontSize: '1.25rem', fontWeight: 600, color: '#1e293b' }}>
            Submitted Declarations
          </Box>
          <Box sx={{ fontSize: '0.813rem', color: '#64748b', mt: 0.25 }}>
            View and manage employee declaration submissions
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Year filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="declaration-year-label">Year</InputLabel>
            <Select
              labelId="declaration-year-label"
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={availableYears.length === 0}
            >
              <MenuItem value="all">All years</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Action Required filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="action-filter-label">Action Required</InputLabel>
            <Select
              labelId="action-filter-label"
              value={actionFilter}
              label="Action Required"
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Pending</MenuItem>
              <MenuItem value="no">Submitted</MenuItem>
            </Select>
          </FormControl>

          {/* Feature 2: Bulk Reminder button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<NotificationsActive />}
            onClick={() => {
              setBulkReminderDeclarationId('');
              setBulkReminderStartDate('');
              setBulkReminderEndDate('');
              setBulkReminderMessage('');
              setBulkReminderOpen(true);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.813rem',
              borderRadius: '8px',
              borderColor: '#f59e0b',
              color: '#d97706',
              '&:hover': { borderColor: '#d97706', background: '#fffbeb' },
            }}
          >
            Bulk Reminder
          </Button>

          {/* Feature 4: Bulk ZIP Download */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdf />}
            onClick={() => {
              setPdfDeclarationId('');
              setPdfDownloadOpen(true);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.813rem',
              borderRadius: '8px',
              borderColor: '#800000',
              color: '#800000',
              '&:hover': { borderColor: '#600000', background: '#fff1f2' },
            }}
          >
            Download Declarations
          </Button>

          {/* Excel report download */}
          <FormModal
            buttonName={'Download Declaration Reports'}
            title={'Download Declaration Reports'}
            form={<DownloadDeclarationReportForm data={allCourses} />}
          />
        </Box>
      </Box>

      {/* Table */}
      <Box className="mt-2">
        <TableComponent
          tableData={filteredCourses}
          setTableData={setAllCourses}
          columns={columns}
          tableName={'Course'}
          count={filteredCourses.length}
          useWholeRowClicked={false}
          enableLocalSearch={true}
        />
      </Box>

      {/* ── Feature 2: Bulk Reminder Dialog ─────────────────────────────────── */}
      <Dialog
        open={bulkReminderOpen}
        onClose={() => !isSendingBulk && setBulkReminderOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Send Bulk Reminders</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ fontSize: '0.875rem', color: '#374151' }}>
              Send reminder emails to all pending employees. Select a specific declaration or leave
              blank to send for all pending in the current year filter.
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>Declaration (optional)</InputLabel>
              <Select
                value={bulkReminderDeclarationId}
                label="Declaration (optional)"
                onChange={(e) => setBulkReminderDeclarationId(e.target.value)}
              >
                <MenuItem value="">All pending (current filter)</MenuItem>
                {uniqueDeclarations.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={bulkReminderStartDate}
              onChange={(e) => setBulkReminderStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank if no start date"
            />
            <TextField
              label="End Date *"
              type="date"
              size="small"
              fullWidth
              required
              value={bulkReminderEndDate}
              onChange={(e) => setBulkReminderEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Deadline by which employees must submit"
            />
            <TextField
              label="Message (optional)"
              multiline
              rows={3}
              fullWidth
              value={bulkReminderMessage}
              onChange={(e) => setBulkReminderMessage(e.target.value)}
              placeholder="Any additional instructions or message for employees..."
              helperText="This message will be included in the reminder email."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkReminderOpen(false)} disabled={isSendingBulk}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkReminderSend}
            disabled={isSendingBulk || !bulkReminderEndDate}
            sx={{ background: '#f59e0b', '&:hover': { background: '#d97706' } }}
          >
            {isSendingBulk ? 'Sending...' : 'Send Reminders'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Feature 4: Download Declarations ZIP Dialog ─────────────────────── */}
      <Dialog
        open={pdfDownloadOpen}
        onClose={() => !isDownloadingPdf && setPdfDownloadOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Download Declarations</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Box sx={{ fontSize: '0.875rem', color: '#374151', mb: 2 }}>
              Select a declaration to download all submitted files as a ZIP. Each file will be named
              with the employee's name, ID, department, and submission date &amp; time.
            </Box>
            <FormControl fullWidth size="small" required>
              <InputLabel>Declaration Name *</InputLabel>
              <Select
                value={pdfDeclarationId}
                label="Declaration Name *"
                onChange={(e) => setPdfDeclarationId(e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select a declaration
                </MenuItem>
                {uniqueDeclarations.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPdfDownloadOpen(false)} disabled={isDownloadingPdf}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkPdfDownload}
            disabled={isDownloadingPdf || !pdfDeclarationId}
            startIcon={<PictureAsPdf />}
            sx={{ background: '#800000', '&:hover': { background: '#600000' } }}
          >
            {isDownloadingPdf ? 'Downloading...' : 'Download ZIP'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Individual Reminder Dialog ──────────────────────────────────────── */}
      <Dialog
        open={reminderDialogOpen}
        onClose={() => !isSendingReminder && setReminderDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Send Reminder</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ fontSize: '0.875rem', color: '#374151' }}>
              Send a reminder to <strong>Employee {reminderRow?.empId}</strong> to submit their
              declaration. Specify the submission window and an optional message.
            </Box>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={reminderStartDate}
              onChange={(e) => setReminderStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank if no start date"
            />
            <TextField
              label="End Date *"
              type="date"
              size="small"
              fullWidth
              required
              value={reminderEndDate}
              onChange={(e) => setReminderEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Deadline by which the employee must submit"
            />
            <TextField
              label="Message (optional)"
              multiline
              rows={3}
              fullWidth
              value={reminderMessage}
              onChange={(e) => setReminderMessage(e.target.value)}
              placeholder="Any additional instructions or message for the employee..."
              helperText="This message will be included in the reminder email."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReminderDialogOpen(false)} disabled={isSendingReminder}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReminderConfirm}
            disabled={isSendingReminder || !reminderEndDate}
            sx={{ background: '#f59e0b', '&:hover': { background: '#d97706' } }}
          >
            {isSendingReminder ? 'Sending...' : 'Send Reminder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Feature 1: Reject Declaration Dialog ────────────────────────────── */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#dc2626' }}>
          Reject Declaration Submission
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Box sx={{ fontSize: '0.875rem', color: '#374151', mb: 2 }}>
              Rejecting this submission will notify <strong>Employee {rejectRow?.empId}</strong> by
              email with your reason and ask them to resubmit.
            </Box>
            <TextField
              label="Reason for rejection"
              multiline
              rows={3}
              fullWidth
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Declaration contains 'NA' instead of 'NIL', or missing signature."
              helperText="This reason will be included in the rejection email sent to the employee."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={isRejecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRejectConfirm}
            disabled={isRejecting}
            sx={{ background: '#dc2626', '&:hover': { background: '#b91c1c' } }}
          >
            {isRejecting ? 'Rejecting...' : 'Reject & Notify Employee'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ViewDeclaration;
